import { Alert, Button, EmptyState, FormField, PRODUCT_NAME, Select, Spinner } from "@station/ui";
import { createFileRoute, Link, Outlet, redirect, useNavigate } from "@tanstack/react-router";
import { Fuel, LogOut, ShieldCheck, Truck } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { PortalAccountProvider, usePortal, usePortalAccounts } from "../features/portal/portal-context.js";
import { portalAccess } from "../features/portal/portal-values.js";
import { errorMessage } from "../lib/errors.js";
import { useDocumentTitle } from "../lib/hooks.js";
import { RealtimeProvider } from "../lib/realtime-context.js";
import { bootstrapSession, logout, useMe } from "../lib/session.js";

export const Route = createFileRoute("/portal")({
  // Oturum yoksa girişe; girişten sonra portala geri dönülür.
  beforeLoad: async ({ location }) => {
    if (!(await bootstrapSession())) throw redirect({ to: "/giris", search: { redirect: location.href } });
  },
  component: PortalLayout,
});

const NAV = [
  { to: "/portal", labelKey: "portal.nav.summary", exact: true },
  { to: "/portal/araclar", labelKey: "portal.nav.vehicles", exact: false },
  { to: "/portal/islemler", labelKey: "portal.nav.transactions", exact: false },
  { to: "/portal/faturalar", labelKey: "portal.nav.invoices", exact: false },
  { to: "/portal/talepler", labelKey: "portal.nav.requests", exact: false },
] as const;

/**
 * Filo PORTALI yerleşimi (Prompt 17 web, ADR 0017 §17): personel menüsü YOKTUR — yalnızca filonun kendi ekranları. Cari
 * kimliği sunucuda atamadan türetilir; personel kullanıcı (portal ataması yok) açık bir ileti görür.
 */
function PortalLayout(): ReactElement {
  const { t } = useTranslation();
  const me = useMe();
  const navigate = useNavigate();
  const tenant = me.data?.activeTenant ?? null;
  const access = portalAccess(tenant);

  if (!me.data) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner label={t("common.loading")} />
      </div>
    );
  }

  const signOut = () => void logout().then(() => navigate({ to: "/giris" }));

  return (
    <RealtimeProvider key={tenant?.id ?? "no-tenant"}>
      <a href="#portal-main" className="skip-link">
        {t("shell.skipToContent")}
      </a>
      <div className="flex min-h-dvh flex-col bg-muted/30">
        <header className="border-b bg-card print:hidden">
          <div className="mx-auto flex w-full max-w-6xl flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3">
            <div className="flex items-center gap-2">
              <Fuel className="size-6 text-primary" aria-hidden="true" />
              <span className="font-semibold">{PRODUCT_NAME}</span>
              <span className="text-sm text-muted-foreground">· {t("portal.title")}</span>
            </div>
            {tenant ? <span className="text-sm text-muted-foreground">{tenant.name}</span> : null}
            <div className="ms-auto flex flex-wrap items-center gap-2">
              <span className="text-sm">{me.data.user.fullName}</span>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/ayarlar/guvenlik">
                  <ShieldCheck aria-hidden="true" />
                  {t("portal.security")}
                </Link>
              </Button>
              <Button variant="outline" size="sm" onClick={signOut}>
                <LogOut aria-hidden="true" />
                {t("shell.userMenu.logout")}
              </Button>
            </div>
          </div>
          {access.hasPortalGrant ? (
            <nav aria-label={t("portal.navLabel")} className="mx-auto w-full max-w-6xl px-2">
              <ul className="flex flex-wrap gap-1">
                {NAV.map((item) => (
                  <li key={item.to}>
                    <Link
                      to={item.to}
                      activeOptions={{ exact: item.exact }}
                      className="inline-flex min-h-10 items-center border-b-2 border-transparent px-3 text-sm font-medium text-muted-foreground hover:text-foreground data-[status=active]:border-primary data-[status=active]:text-foreground"
                    >
                      {t(item.labelKey)}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>
          ) : null}
        </header>
        <main
          id="portal-main"
          tabIndex={-1}
          className="mx-auto w-full max-w-6xl flex-1 p-4 outline-none sm:p-6"
        >
          {tenant === null ? (
            <Alert tone="warning" title={t("shell.noTenant.title")}>
              {t("shell.noTenant.description")}
            </Alert>
          ) : !access.hasPortalGrant ? (
            <NoPortalAccess />
          ) : (
            <PortalAccounts tenantId={tenant.id} />
          )}
        </main>
      </div>
    </RealtimeProvider>
  );
}

function NoPortalAccess(): ReactElement {
  const { t } = useTranslation();
  useDocumentTitle(t("portal.title"));
  return (
    <EmptyState
      icon={<Truck />}
      title={t("portal.noAccessTitle")}
      description={t("portal.noAccessDescription")}
      action={
        <Button asChild>
          <Link to="/">{t("portal.toStaffPanel")}</Link>
        </Button>
      }
    />
  );
}

function PortalAccounts({ tenantId }: { tenantId: string }): ReactElement {
  const { t } = useTranslation();
  const accounts = usePortalAccounts();
  if (accounts.error) return <Alert tone="danger">{errorMessage(accounts.error)}</Alert>;
  if (!accounts.data)
    return (
      <div className="flex justify-center py-10">
        <Spinner label={t("common.loading")} />
      </div>
    );
  if (accounts.data.length === 0)
    return (
      <EmptyState
        icon={<Truck />}
        title={t("portal.noAccountsTitle")}
        description={t("portal.noAccountsDescription")}
      />
    );
  return (
    <PortalAccountProvider tenantId={tenantId} accounts={accounts.data}>
      <AccountSwitcher />
      <Outlet />
    </PortalAccountProvider>
  );
}

/** Birden çok filoya erişimi olan kullanıcı için cari seçici; tek filoda yalnızca adı yazılır. */
function AccountSwitcher(): ReactElement {
  const { t } = useTranslation();
  const portal = usePortal();
  if (portal.accounts.length <= 1)
    return (
      <p className="mb-4 text-sm text-muted-foreground">
        {t("portal.accountLabel")}:{" "}
        <span className="font-medium text-foreground">{portal.account.counterpartyName}</span>
      </p>
    );
  return (
    <div className="mb-4 max-w-sm">
      <FormField label={t("portal.selectAccount")}>
        {(control) => (
          <Select
            {...control}
            value={portal.counterpartyId}
            onChange={(event) => portal.select(event.target.value)}
          >
            {portal.accounts.map((account) => (
              <option key={account.counterpartyId} value={account.counterpartyId}>
                {account.counterpartyCode} · {account.counterpartyName}
              </option>
            ))}
          </Select>
        )}
      </FormField>
    </div>
  );
}
